import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { usePage, useSession, useSources } from "../data/usePage.js";
import { useRepo, repoRelativePath, repoHref } from "../data/repo.js";
import { useFindHighlight } from "../markdown/useFindHighlight.js";
import { PageEditor, type EditMode } from "../editor/PageEditor.js";
import { useLocalDraft, RestoredNotice } from "../editor/useLocalDraft.js";
import { Markdown } from "../markdown/Markdown.js";
import { DwSqlProvider, PageFilters } from "../data/DwSql.js";
import { useDebounced } from "../data/useDebounced.js";
import { QueryViz } from "../viz/Query.js";
import { saveContent, postCommit, deleteFile, moveFile, copyFile, checkQuery } from "../api.js";
import { ExportUrlCard } from "./ExportUrlCard.js";
import { invalidate } from "../data/useFetch.js";
import { EDIT_SLOT_ID } from "../layout/RightSidebar.js";
import { EditTopButton } from "../layout/EditTopButton.js";
import { ShareBadge } from "../layout/ShareBadge.js";
import { Dialog } from "../layout/Dialog.js";
import { configTypeFor, validateConfig } from "../config/configTypes.js";
import type { PageResponse } from "../../shared/types.js";

const MD = /\.(md|markdown)$/;

/** The `-- @source: <id>` a query names, or null when it takes the default.
 * Mirrors the server's sourceDirective. */
function querySourceId(sql: string): string | null {
  const m = /^\s*--\s*@source:\s*([\w-]+)/im.exec(sql);
  return m ? m[1].toLowerCase() : null;
}

export function PageView() {
  const loc = useLocation();
  const repo = useRepo();
  const reqPath = repoRelativePath(loc.pathname, repo);
  const p = usePage(reqPath);
  const session = useSession();

  if (p.status === "loading") return null;
  if (p.status === "error")
    return <pre className="dw-error">{p.error.message}</pre>;

  const page = p.data;
  const editable =
    session.status === "ready" &&
    session.data.editing &&
    (page.kind === "md" ||
      page.kind === "sql" ||
      page.kind === "access" ||
      page.kind === "config") &&
    page.can.includes("edit");

  if (page.kind === "none") {
    return (
      <article className="dw-doc">
        <h1>Not found</h1>
        <p>
          <code>{reqPath || "/"}</code> doesn't exist.
        </p>
      </article>
    );
  }
  if (page.kind === "dir") {
    const base = page.path ? `${page.path}/` : "";
    return (
      <article className="dw-doc">
        <h1>
          {page.path || "/"}
          {page.shares !== undefined && (
            <ShareBadge repo={repo} path={page.path} dir count={page.shares} />
          )}
        </h1>
        <ul className="dw-tree">
          {page.entries.length === 0 && <li>(empty)</li>}
          {page.entries.map((e) => (
            <li key={e.name} className={e.isDir ? "dw-dir" : "dw-file"}>
              <Link to={repoHref(repo, `${base}${e.name}`)}>
                {e.name}
                {e.isDir ? "/" : ""}
              </Link>
            </li>
          ))}
        </ul>
      </article>
    );
  }
  if (page.kind === "sql") {
    return <SqlPage page={page} editable={editable} />;
  }
  if (page.kind === "access") {
    return <AccessPage page={page} editable={editable} />;
  }
  if (page.kind === "config") {
    return <ConfigEditor page={page} editable={editable} />;
  }
  if (page.kind === "raw") {
    return (
      <article className="dw-doc">
        <h1>
          <code>{page.path}</code>
        </h1>
        <pre className="dw-raw">
          <code>{page.content}</code>
        </pre>
        <p className="dw-muted">
          Read-only — datawiki edits markdown, query, and access files.
        </p>
      </article>
    );
  }
  return <MarkdownPage page={page} editable={editable} />;
}

const ACCESS_LEVELS = ["inherit", "none", "read", "full"] as const;

/**
 * A `.datawiki/access` file as a constrained subject→level form (not markdown).
 * "inherit" means no rule for that subject. Save writes a draft; publish via the
 * normal commit flow applies it. See docs/auth/Hats.md.
 */
function AccessPage({
  page,
  editable,
}: {
  page: PageResponse & { kind: "access" };
  editable: boolean;
}) {
  const repo = useRepo();
  // Existing rules may name a subject no longer in `subjects` (e.g. a removed
  // group) — keep it editable so saving doesn't silently drop it.
  const subjects = [...new Set([...page.subjects, ...Object.keys(page.rules)])];
  const initial = (): Record<string, string> =>
    Object.fromEntries(subjects.map((s) => [s, page.rules[s] ?? "inherit"]));
  const [sel, setSel] = useState<Record<string, string>>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resync when the underlying file changes (navigation, post-save refetch).
  const ruleKey = JSON.stringify(page.rules) + subjects.join(",");
  useEffect(() => setSel(initial()), [page.path, ruleKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = subjects.some((s) => (sel[s] ?? "inherit") !== (page.rules[s] ?? "inherit"));
  const serialize = (): string =>
    subjects
      .filter((s) => sel[s] && sel[s] !== "inherit")
      .map((s) => `${s}: ${sel[s]}`)
      .join("\n") + "\n";

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await saveContent(repo, page.path, serialize());
      invalidate("tree");
      invalidate("commit");
      invalidate("page:");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className="dw-doc dw-access-editor">
      <h1>
        <code>{page.path}</code>
      </h1>
      <p className="dw-muted">
        Who can do what in this directory and below. Deepest{" "}
        <code>.datawiki/access</code> wins; effective level is the max across a
        user's groups. <code>inherit</code> = no rule. Save creates a draft —
        publish to apply.
      </p>
      <table className="dw-access-table">
        <tbody>
          {subjects.map((s) => (
            <tr key={s}>
              <td>
                <code className="dw-access-subject">{s}</code>
              </td>
              <td>
                <select
                  value={sel[s] ?? "inherit"}
                  disabled={!editable || busy}
                  onChange={(e) => setSel((p) => ({ ...p, [s]: e.target.value }))}
                >
                  {ACCESS_LEVELS.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {editable ? (
        <button className="dw-btn" type="button" disabled={!dirty || busy} onClick={save}>
          {busy ? "Saving…" : "Save draft"}
        </button>
      ) : (
        <p className="dw-muted">read-only — you need full access here to edit.</p>
      )}
      {error && <p className="dw-error">{error}</p>}
    </article>
  );
}

/**
 * A datawiki-config file as a raw YAML textarea — the brutalist baseline (every
 * config value is a YAML box). Save writes a draft; publish applies it (the config
 * repo hot-reloads on push). Access files get the constrained form instead.
 *
 * Per-file niceties — help text, a pre-save validator, and an extra UI panel
 * (e.g. the GitHub device flow) — are data-driven: looked up by path in
 * config/configTypes. A file with no entry is just the plain box.
 */
function ConfigEditor({
  page,
  editable,
}: {
  page: PageResponse & { kind: "config" };
  editable: boolean;
}) {
  const repo = useRepo();
  const type = configTypeFor(page.path);
  const [text, setText] = useState(page.content);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setText(page.content), [page.path, page.content]);
  const dirty = text !== page.content;
  // A validation failure blocks the save and is shown inline; null = ok. Every
  // config file is gated on parsing as YAML, whether or not it has a type.
  const invalid = validateConfig(page.path, text);

  const save = async (): Promise<void> => {
    if (invalid) return;
    setBusy(true);
    setError(null);
    try {
      await saveContent(repo, page.path, text);
      invalidate("tree");
      invalidate("commit");
      invalidate("page:");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className="dw-doc dw-access-editor">
      <h1>
        <code>{page.path}</code>
      </h1>
      <p className="dw-muted">
        <code>datawiki-config</code> — raw YAML. Save creates a draft; publish to apply.
      </p>
      {type?.instructions}
      <textarea
        className="dw-config-text"
        value={text}
        disabled={!editable || busy}
        spellCheck={false}
        rows={Math.max(6, text.split("\n").length + 1)}
        onChange={(e) => setText(e.target.value)}
      />
      {/* Shown dirty or not: a committed file that doesn't parse is exactly the
          thing an admin needs told, and it's why the setting isn't applying. */}
      {invalid && <p className="dw-error">{invalid}</p>}
      {editable ? (
        <button
          className="dw-btn"
          type="button"
          disabled={!dirty || busy || !!invalid}
          onClick={save}
        >
          {busy ? "Saving…" : "Save draft"}
        </button>
      ) : (
        <p className="dw-muted">read-only — you need full access here to edit.</p>
      )}
      {error && <p className="dw-error">{error}</p>}
      {type?.ExtraUI && (
        <type.ExtraUI
          text={text}
          setText={setText}
          repo={repo}
          path={page.path}
          editable={editable}
        />
      )}
    </article>
  );
}

// A query file: read-only run view, or (?edit) an inline editor with the query
// result as a live preview below the textarea.
function SqlPage({
  page,
  editable,
}: {
  page: PageResponse & { kind: "sql" };
  editable: boolean;
}) {
  const repo = useRepo();
  // Queries start in edit mode (no read-only step) and do NOT autosave —
  // running a half-written query is worse than markdown, so you Save explicitly.
  const editing = editable;
  // Mirrored to localStorage; follows page.content only while clean.
  const { draft, setDraft, restored, discardRestored, clearMirror } =
    useLocalDraft(page.path, page.content);
  const [busy, setBusy] = useState<"" | "save" | "publish">("");
  const [error, setError] = useState<string | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
  }, [page.content]);

  const dirty = editable && draft !== page.content;

  // This query's data source and whether it's reachable. Down ⇒ saving is
  // blocked (we'd be storing a query we can't validate). The status re-pings
  // when the data card refreshes (invalidate("sources")) — the way back to UP.
  const sources = useSources();
  const srcId = querySourceId(draft);
  // No `@source:` ⇒ whichever source the server reports as the default (the
  // default database), so this doesn't need to know which one that is.
  const source =
    sources.status === "ready"
      ? sources.data.find((s) => (srcId ? s.id === srcId : s.isDefault))
      : undefined;
  const sourceDown = source ? !source.up : false;

  // No nav guard: unsaved edits are recoverable from the localStorage mirror,
  // so navigating away (or reloading) never loses work.

  const save = async (publish: boolean): Promise<void> => {
    if (sourceDown) return; // can't save against a down source (button disabled)
    setBusy(publish ? "publish" : "save");
    setError(null);
    setCheckError(null);
    try {
      // Validate BEFORE writing — the invariant is that every saved query is
      // valid. A failed check (bad SQL, or a source that just went down) blocks
      // the save; the draft stays in the editor + its localStorage mirror.
      const c = await checkQuery(repo, draft, page.path);
      if (!c.ok) {
        setCheckError(`${c.stage ?? "check"}: ${c.error ?? "failed"}`);
        return;
      }
      await saveContent(repo, page.path, draft);
      clearMirror(); // saved to the worktree — mirror done
      if (publish) await postCommit(repo, "", "merge");
      invalidate("page:");
      invalidate("commit");
      invalidate("tree");
      invalidate("session");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy("");
    }
  };

  const slot =
    typeof document !== "undefined"
      ? document.getElementById(EDIT_SLOT_ID)
      : null;
  // Note under the buttons: data-source-down warning (orange) takes priority
  // over an invalid-query error (red).
  const note = sourceDown ? (
    <span className="dw-source-down">Data Source Down</span>
  ) : checkError ? (
    <span className="dw-save-error">{checkError}</span>
  ) : null;
  // Top action: "Save" while there are unsaved edits, "Publish" once clean.
  const topButton =
    editing && slot
      ? createPortal(
          <EditTopButton
            kind="sql"
            editing={editing}
            dirty={dirty}
            busy={busy}
            onEdit={() => {}}
            onSave={() => save(false)}
            onPublish={() => save(true)}
            block={sourceDown}
            note={note}
          />,
          slot,
        )
      : null;

  // While editing, re-run the preview only after typing pauses — otherwise
  // every keystroke fires a fresh query against the source. Fixed 400ms for now;
  // a slow-query-aware backoff is planned (docs/sql/plans.md#adaptive-preview-debounce).
  const debouncedDraft = useDebounced(draft, 400);
  const previewSource = editing ? debouncedDraft : page.content;
  return (
    <article className="dw-doc">
      {topButton}
      <ExportUrlCard path={page.path} />
      <h1>{page.path}</h1>
      <RestoredNotice restored={restored} onDiscard={discardRestored} />
      {editing ? (
        <textarea
          className="dw-textarea dw-sql-editor"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (checkError) setCheckError(null); // stale once you edit
          }}
          spellCheck={false}
          autoFocus
        />
      ) : (
        <pre className="dw-sql">
          <code>{page.content}</code>
        </pre>
      )}
      <DwSqlProvider path={page.path}>
        <PageFilters />
        <QueryViz source={previewSource} attrs={{}} />
      </DwSqlProvider>
      {error && <pre className="dw-error">{error}</pre>}
    </article>
  );
}

function MarkdownPage({
  page,
  editable,
}: {
  page: PageResponse & { kind: "md" };
  editable: boolean;
}) {
  const navigate = useNavigate();
  const repo = useRepo();
  const [searchParams, setSearchParams] = useSearchParams();
  // ?edit swaps the rendered page for the editor (docs/wiki/EditModes.md).
  // Without it the page is read-only prose whose embeds are still editable
  // inline, against the same draft.
  const editing = editable && searchParams.has("edit");
  // Rich by default; the switch lives in the right sidebar (PageEditor). Kept
  // here so flipping doesn't unmount the draft along with the editor.
  const [mode, setMode] = useState<EditMode>("rich");
  // Draft state is mirrored to localStorage (crash/reload recovery) and only
  // follows page.content while clean — typed text survives a background sync
  // refetching the page underneath us.
  const { draft, setDraft, restored, discardRestored, clearMirror } =
    useLocalDraft(page.path, page.content);
  const [busy, setBusy] = useState<"" | "save" | "publish">("");
  const [error, setError] = useState<string | null>(null);
  // Publish outcome that warrants a modal (vs the inline `error`): a publish
  // that merged locally but couldn't push, or that failed outright. Shown in a
  // dialog with a link to the repo page rather than stuck on the page.
  const [publishResult, setPublishResult] = useState<{
    tone: "warn" | "error";
    title: string;
    message: string;
  } | null>(null);
  // The draft last persisted to the worktree (by autosave or publish). Drives
  // `dirty` so the nav guard clears once changes are saved — without refetching
  // the page (the rendered draft already IS the preview).
  const [savedContent, setSavedContent] = useState(page.content);

  // In-document search highlighting: a `?find=` from a search result paints
  // matches over the rendered page via the CSS Highlight API. It targets the
  // read-only render — inside an editor the DOM belongs to ProseMirror (rich)
  // or is a textarea (markdown), and painting over either would fight it.
  const docRef = useRef<HTMLElement>(null);
  const findQuery = searchParams.get("find") ?? "";
  useFindHighlight(docRef, editing ? "" : findQuery, page.content);

  // The draft follows page.content only while clean — useLocalDraft never
  // clobbers typed text. No nav guard: unsaved edits autosave to the worktree
  // and are mirrored to localStorage, so navigating away never loses work.
  useEffect(() => {
    setError(null);
    setSavedContent(page.content);
  }, [page.content]);

  const dirty = editable && draft !== savedContent;

  const exitEditing = (): void => {
    setSearchParams({}, { replace: true });
  };

  const invalidateAfterWrite = (): void => {
    invalidate("page:");
    invalidate("commit");
    invalidate("tree");
    invalidate("session");
    // A write may have changed a query file (or an embed's SQL) — drop cached
    // query results so embeds re-run against the new content instead of serving
    // the stale client cache.
    invalidate("query:");
  };

  // Persist the current draft to the worktree (no publish, no page refetch).
  // Read through refs so the debounce/flush always sees the latest text.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const savedRef = useRef(savedContent);
  savedRef.current = savedContent;
  // The draft text whose last save attempt FAILED. Autosave won't retry the
  // same text (a failing save toggles `busy`, which would otherwise re-fire the
  // effect forever) — only an edit (new text) or an explicit close retries.
  const failedRef = useRef<string | null>(null);
  const persistDraft = async (): Promise<boolean> => {
    const text = draftRef.current;
    if (text === savedRef.current) return true; // nothing new to save
    setBusy("save");
    setError(null);
    try {
      await saveContent(repo, page.path, text);
      failedRef.current = null;
      setSavedContent(text);
      clearMirror(); // saved to the worktree — mirror done
      // Refresh page caches so navigating away and back shows the saved text
      // (not the pre-edit copy). The page cache key is the URL path (e.g.
      // ".../Foo", no extension), which differs from page.path (".../Foo.md")
      // — so invalidate by repo prefix, not by path. Stale-while-revalidate +
      // fetched content == draft means no flicker.
      invalidate(`page:${repo}/`);
      invalidate("commit");
      invalidate("tree");
      invalidate("session");
      return true;
    } catch (e) {
      failedRef.current = text;
      setError(e instanceof Error ? e.message : String(e));
      return false;
    } finally {
      setBusy("");
    }
  };

  // Autosave: persist the draft a beat after typing stops. The Save button is
  // gone — saving is automatic; Save + Publish is the only explicit action.
  useEffect(() => {
    if (!editable || busy === "publish" || draft === savedContent) return;
    if (draft === failedRef.current) return; // don't hammer a failing save
    const id = setTimeout(() => {
      void persistDraft();
    }, 800);
    return () => clearTimeout(id);
    // persistDraft reads refs; re-running only on draft/savedContent is correct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, savedContent, editable, busy]);

  // Leave the editor: flush a save first, then drop back to the rendered page.
  // Stay open on failure so the error is actionable and nothing is lost.
  const closeEditor = async (): Promise<void> => {
    if (await persistDraft()) exitEditing();
  };

  const savePublish = async (): Promise<void> => {
    setBusy("publish");
    setError(null);
    try {
      await saveContent(repo, page.path, draft);
      clearMirror();
      const r = await postCommit(repo, "", "merge");
      // Refresh either way — the local merge landed, so the worktree/badge is
      // gone even when the push didn't.
      invalidateAfterWrite();
      if (editing) exitEditing();
      if (r.pushError) {
        setPublishResult({
          tone: "warn",
          title: "Published locally — not pushed",
          message: r.pushError,
        });
      }
    } catch (e) {
      setPublishResult({
        tone: "error",
        title: "Publish failed",
        message: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy("");
    }
  };

  // Rename/move the file in the draft, then follow it to its new URL.
  const onRename = async (): Promise<void> => {
    const to = window.prompt("Rename / move to (repo path):", page.path);
    if (!to || to === page.path) return;
    setError(null);
    try {
      const out = await moveFile(repo, page.path, to);
      invalidateAfterWrite();
      navigate(repoHref(repo, out.to.replace(MD, "")));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  // Duplicate the file under a new name (defaulting to "<name> copy.ext"),
  // then open the copy.
  const onCopy = async (): Promise<void> => {
    const base = page.path.replace(/^.*\//, "");
    const ext = /\.[^./]+$/.exec(base)?.[0] ?? "";
    const dir = page.path.includes("/") ? page.path.replace(/\/[^/]*$/, "") : "";
    const suggested = `${base.slice(0, base.length - ext.length)} copy${ext}`;
    const name = window.prompt("Copy to (file name):", suggested);
    if (!name) return;
    const target = name.includes("/") ? name : dir ? `${dir}/${name}` : name;
    setError(null);
    try {
      const out = await copyFile(repo, page.path, target);
      invalidateAfterWrite();
      navigate(repoHref(repo, out.to.replace(MD, "")));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  // Delete the file from the draft (staged until publish), then go to its parent.
  const onDelete = async (): Promise<void> => {
    if (
      !window.confirm(
        `Delete ${page.path}? It stays staged in your draft until you publish.`,
      )
    )
      return;
    setError(null);
    try {
      await deleteFile(repo, page.path);
      invalidateAfterWrite();
      navigate(
        repoHref(repo, page.path.includes("/") ? page.path.replace(/\/[^/]*$/, "") : ""),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  // Non-editors: plain, read-only render — no chrome, no interactivity.
  if (!editable) {
    return (
      <article className="dw-doc" ref={docRef}>
        <DwSqlProvider path={page.path}>
          <PageFilters />
          <Markdown source={page.content} path={page.path} repo={repo} />
        </DwSqlProvider>
      </article>
    );
  }

  const slot =
    typeof document !== "undefined"
      ? document.getElementById(EDIT_SLOT_ID)
      : null;
  // Top action: "Edit" (open the editor) when viewing, "Publish" while editing.
  // Markdown autosaves, so there's no Save button — "done" persists and closes.
  const topButton = slot
    ? createPortal(
        <EditTopButton
          kind="md"
          editing={editing}
          dirty={dirty}
          busy={busy}
          onEdit={() => setSearchParams({ edit: "" })}
          onSave={() => {}}
          onPublish={savePublish}
        />,
        slot,
      )
    : null;

  // File tools (copy/rename/delete) below the top button, except while editing.
  // Same look as the top actions, but its own class: on a narrow window the top
  // actions float over the content as icons and these stay behind ⎇ (styles.css,
  // RESPONSIVE) — page management isn't a primary action.
  const fileTools =
    !editing && slot
      ? createPortal(
          <section className="dw-card dw-edit-actions dw-file-tools">
            {page.shares !== undefined && (
              <ShareBadge
                repo={repo}
                path={page.path.replace(/\.(md|markdown)$/i, "")}
                dir={false}
                count={page.shares}
              />
            )}
            <button onClick={onCopy}>Make a Copy…</button>
            <button onClick={onRename}>Rename…</button>
            <button onClick={onDelete} className="dw-link dw-danger-link">
              Delete…
            </button>
          </section>,
          slot,
        )
      : null;

  // "done" — save and drop back to the rendered page. Sits beside the mode
  // switch the editor puts in the same sidebar slot. Label + icon like the
  // other top actions: on a narrow window this card floats over the content
  // and CSS shows the glyph instead (styles.css, RESPONSIVE).
  const doneButton =
    editing && slot
      ? createPortal(
          <section className="dw-card dw-edit-actions">
            <button
              type="button"
              data-testid="dw-edit-done"
              onClick={() => void closeEditor()}
              title="Save and go back to the page"
            >
              <span className="dw-btn-label">Done editing</span>
              <span className="dw-btn-icon" aria-hidden="true">
                ✓
              </span>
            </button>
          </section>,
          slot,
        )
      : null;

  // An editor's page. Viewing: the rendered draft, embeds editable inline via
  // their own double-click modal. Editing (`?edit`): the two-mode editor —
  // Milkdown, or per-block raw markdown — sharing that same draft, so a flip
  // between them costs nothing. Both sit inside DwSqlProvider so an embed
  // behaves identically wherever it is rendered.
  return (
    <article className={`dw-doc${editing ? " dw-doc-editing" : ""}`} ref={docRef}>
      {topButton}
      {fileTools}
      {doneButton}
      <RestoredNotice restored={restored} onDiscard={discardRestored} />
      <DwSqlProvider path={page.path}>
        <PageFilters />
        {editing ? (
          <PageEditor
            draft={draft}
            setDraft={setDraft}
            path={page.path}
            repo={repo}
            mode={mode}
            setMode={setMode}
          />
        ) : (
          <Markdown
            source={draft}
            onSourceChange={setDraft}
            path={page.path}
            repo={repo}
            onRequestEdit={() => setSearchParams({ edit: "" })}
          />
        )}
      </DwSqlProvider>
      {error && <pre className="dw-error">{error}</pre>}
      {publishResult && (
        <Dialog
          title={publishResult.title}
          tone={publishResult.tone}
          onClose={() => setPublishResult(null)}
        >
          <pre>{publishResult.message}</pre>
          <p>
            See{" "}
            <Link to={`/_repo/${repo}`} onClick={() => setPublishResult(null)}>
              the repo page
            </Link>{" "}
            to push to GitHub or check the connection.
          </p>
        </Dialog>
      )}
    </article>
  );
}
